import { HttpStatusCode } from 'axios';
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { ClipLoader } from 'react-spinners';
import { Modal, Input } from 'antd';

import useApi from '../../../../../utils/AxiosWrapper';
import { useScreenView } from '../../../../../utils/ScreenViewContext';
import { useUser } from '../../../../../utils/UserContext';
import { Entity } from '../../../../../types/apiTypes';
import { AccountDeletionRequest, Customer } from '../../../../../types/dbTypes';
import {
  AccentButtonOutline,
  WarningButton,
} from '../../../../../shared/Buttons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus } from '@phosphor-icons/react';
import { AddressCard } from './AddressCard';
import AddNewAddressModal from './widgets/AddNewAddressModal';

interface AccountSettingsProps {
  userType: string;
  currentPage: string;
  currentTab: string;
  setCurrentTab: (tab: string) => void;
  subTab: string;
  setSubTab: (tab: string) => void;
  isPreActive: boolean;
}

function AccountSettings({
  userType,
  currentPage,
  currentTab,
  setCurrentTab,
  subTab,
  setSubTab,
  isPreActive,
}: AccountSettingsProps) {
  const { callProtectedApi } = useApi();
  const { screenView } = useScreenView();
  const [loading, setLoading] = useState(false);
  const { userRecord, userId } = useUser();
  const [deletionRequest, setDeletionRequest] =
    useState<AccountDeletionRequest | null>(null);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [newAddressPopupVisible, setNewAddressPopupVisible] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [loadingMakePrimary, setLoadingMakePrimary] = useState(false);
  const [showAllAddresses, setShowAllAddresses] = useState(false);

  const [activePopup, setActivePopup] = useState<string | null>(null);
  const popupRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const queryClient = useQueryClient();

  const handleClickOutside = (event: MouseEvent) => {
    if (!activePopup) return;

    const clickedPopup = addresses.find((address: any) => {
      const popupRef = popupRefs.current[address?.address_id ?? ''];
      return popupRef?.contains(event.target as Node);
    });

    if (!clickedPopup) {
      setActivePopup(null);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePopup]);

  useEffect(() => {
    setLoading(true);
    fetchAccountDeletionRequest(userId ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAccountDeletionRequest = async (customer_id: string) => {
    try {
      const response = await callProtectedApi(
        `/accountDeletionRequests/fetch/${Entity.Customer}/${customer_id}`,
        'get',
        {},
        'Error occurred while fetching account deletion request.'
      );
      const request = response?.data?.data?.deletionRequest;
      if (request && HttpStatusCode.Ok && Object.entries(request)?.length > 0) {
        setDeletionRequest(request);
      }
    } catch (error: any) {
      console.error('Error fetching account deletion request:', error);
    } finally {
      setLoading(false);
    }
  };

  const { data: addresses = [], isLoading: loadingAddresses } = useQuery({
    queryKey: ['addresses', userId ?? ''],
    queryFn: async () => {
      const response = await callProtectedApi(
        `/addresses/list/${userId ?? ''}`,
        'get',
        {},
        'Error occurred while fetching your addresses.'
      );
      return response?.data?.data?.addresses || [];
    },
    enabled: !!userId,
    staleTime: 15 * 60 * 1000, // 15 minutes
    gcTime: 15 * 60 * 1000, // 15 minutes
  });

  const sortedAddresses = useMemo(() => {
    if (!addresses || addresses.length === 0) return [];
    return [...addresses].sort(
      (a, b) => (b.is_primary ? 1 : 0) - (a.is_primary ? 1 : 0)
    );
  }, [addresses]);

  const displayedAddresses = useMemo(() => {
    if (!sortedAddresses) return [];
    return showAllAddresses ? sortedAddresses : sortedAddresses.slice(0, 2);
  }, [sortedAddresses, showAllAddresses]);

  // If a popup is open for an item that gets hidden when collapsing, close it.
  useEffect(() => {
    if (!showAllAddresses && activePopup) {
      const stillVisible = displayedAddresses.some(
        (a: any) => a.address_id === activePopup
      );
      if (!stillVisible) setActivePopup(null);
    }
  }, [showAllAddresses, activePopup, displayedAddresses]);

  const togglePopup = (address_id: string | undefined) => {
    if (!address_id) return;
    setActivePopup(activePopup === address_id ? null : address_id);
  };

  const createAccountDeletionRequest = async () => {
    try {
      const response = await callProtectedApi(
        `/accountDeletionRequests/create/${Entity.Customer}/${userId}`,
        'post',
        { reason: 'I want to delete my account.' },
        'Error occurred while creating account deletion request.'
      );
      if (response?.status === HttpStatusCode.Created) {
        toast.success('Account deletion request created successfully');
      }
    } catch (e: any) {
      console.error(
        'Error occurred while sending account deletion request: ',
        e?.message
      );
    }
  };

  const setAccountAsPrimary = async (address_id: string) => {
    try {
      setLoadingMakePrimary(true);

      const response = await callProtectedApi(
        `/addresses/${address_id}/setPrimary`,
        'post',
        {},
        'Error occurred while setting account as primary.'
      );
      if (response?.status === HttpStatusCode.Ok) {
        toast.success('Address set as primary successfully');
        queryClient.invalidateQueries({
          queryKey: ['addresses', userId ?? ''],
        });
      }
    } catch (e: any) {
      console.error(
        'Error occurred while setting account as primary: ',
        e?.message
      );
    } finally {
      setLoadingMakePrimary(false);
    }
  };
  const deleteAddress = async (address_id: string) => {
    try {
      setLoadingDelete(true);

      const response = await callProtectedApi(
        `/addresses/${address_id}/delete`,
        'post',
        {},
        'Error occurred while deleting address.'
      );
      if (response?.status === HttpStatusCode.Ok) {
        toast.success('Address deleted successfully');
        queryClient.invalidateQueries({
          queryKey: ['addresses', userId ?? ''],
        });
      }
    } catch (e: any) {
      console.error(
        'Error occurred while setting account as primary: ',
        e?.message
      );
    } finally {
      setLoadingDelete(false);
    }
  };

  const showDeleteModal = () => {
    setIsDeleteModalVisible(true);
    setDeleteConfirmText('');
  };

  const handleDeleteCancel = () => {
    setIsDeleteModalVisible(false);
    setDeleteConfirmText('');
  };

  const handleDeleteConfirm = async () => {
    if (deleteConfirmText === 'delete') {
      setIsDeleteModalVisible(false);
      await createAccountDeletionRequest();
    } else {
      toast.error('Please type "delete" to confirm');
    }
  };

  const renderScreenView = (screenView: string) => {
    switch (screenView) {
      case 'desktop':
      case 'tablet':
      case 'mobile':
        return (
          <div className="flex h-full min-h-[80vh] w-full flex-col items-center justify-start gap-y-6 overflow-y-auto overflow-x-hidden border-[1px] border-[#EAECF0] bg-white px-2 py-6 md:p-6">
            <div className="flex w-full flex-col items-start justify-between space-y-2 align-middle md:w-[95%] md:flex-row md:items-center lg:w-[80%]">
              <h2 className="text-[22px] font-semibold text-primary">
                Account Management
              </h2>
            </div>
            {!loading || !loadingAddresses ? (
              <div className="flex w-full flex-col items-center justify-between gap-6">
                <div className="flex w-full flex-col items-center space-y-2">
                  <div className="mb-4 flex w-[95%] items-center justify-between lg:w-[80%]">
                    <h3 className="text-sm font-medium text-graysecondary">
                      Membership
                    </h3>
                  </div>
                  <div className="flex w-full flex-col items-center justify-between gap-4 md:w-[95%] lg:w-[80%]">
                    <div className="flex w-full flex-row items-center justify-between rounded-[4px] border-[1px] border-bordergray px-4 py-4">
                      <span className="text-sm font-medium text-graysecondary">
                        TACH Member ID
                      </span>
                      <span className="text-sm font-semibold text-primary">
                        {(userRecord as Customer)?.tach_member_id ?? '—'}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex w-full flex-col items-center space-y-2">
                  <div className="flex w-[95%] items-center justify-between lg:w-[80%]">
                    <h3 className="text-sm font-medium text-graysecondary">
                      Addresses
                    </h3>
                    <AccentButtonOutline
                      onClick={() => setNewAddressPopupVisible(true)}
                      addClass={`rounded-full w-full max-w-[200px] self-end !py-1.5`}
                    >
                      <div className="flex flex-row items-center space-x-2 align-middle">
                        <Plus size={20} />
                        <p>Add New Address</p>
                      </div>
                    </AccentButtonOutline>
                  </div>
                  <div className="flex w-full flex-col items-center justify-between gap-4 pt-2 md:w-[95%] lg:w-[80%]">
                    {sortedAddresses?.length > 0 ? (
                      <ul className="w-full space-y-3">
                        {displayedAddresses.map((addr: any) => (
                          <li key={addr.address_id}>
                            <AddressCard
                              address={{
                                address_id: addr.address_id,
                                address_st_number: addr.address_st_number,
                                address_st_name: addr.address_st_name,
                                address_unit: addr.address_unit,
                                city: addr.address_city,
                                state: addr.address_state,
                                postal_code: addr.address_zip,
                                is_primary: addr.is_primary ?? false,
                              }}
                              togglePopup={togglePopup}
                              deleteAddress={async (id: string) =>
                                deleteAddress(id)
                              }
                              makePrimary={async (id: string) =>
                                setAccountAsPrimary(id)
                              }
                              loadingDelete={loadingDelete}
                              loadingMakePrimary={loadingMakePrimary}
                              activePopup={activePopup}
                              popupRefs={popupRefs}
                            />
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <div className="flex h-full w-full flex-col items-center justify-center space-y-2">
                        <div className="mb-4 flex h-[64px] w-[64px] items-center justify-center rounded-[4px] border-[1px] border-bordergray bg-[#F5F6F7]">
                          <img
                            src="/receipt-search.svg"
                            alt="Invoice Icon"
                            className="text-graysecondary"
                          />
                        </div>
                        <p className="text-[16px] font-semibold leading-[20px] text-primary">
                          You don’t have any addresses yet
                        </p>
                        <p className="text-[14px] leading-[20px] text-graysecondary">
                          Please add an address to use it for billing and
                          delivery.
                        </p>
                      </div>
                    )}
                    {sortedAddresses?.length > 2 && (
                      <div className="w-full">
                        <button
                          type="button"
                          onClick={() => setShowAllAddresses((s) => !s)}
                          className="mt-2 inline-flex gap-2 px-3 text-sm font-medium text-primary hover:underline"
                          aria-expanded={showAllAddresses}
                          aria-controls="address-list"
                        >
                          {showAllAddresses
                            ? 'Hide addresses'
                            : 'View full address list'}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex w-full flex-col items-center justify-center space-y-2">
                  <div className="mb-4 flex w-[95%] items-center justify-between lg:w-[80%]">
                    <h3 className="text-sm font-medium text-graysecondary">
                      Account Deletion
                    </h3>
                  </div>
                  <div className="flex w-full flex-col items-center justify-between space-y-4 rounded-[4px] border-[1px] border-bordergray px-4 py-4 md:w-[95%] lg:w-[80%] lg:flex-row lg:space-y-0">
                    <WarningButton
                      addClass="h-[38px] rounded-full px-2"
                      disabled={!!deletionRequest}
                      onClick={showDeleteModal}
                    >
                      Request to Delete Account
                    </WarningButton>
                    {deletionRequest && (
                      <p>Account deletion request submitted.</p>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex h-full w-full items-center justify-center">
                <ClipLoader
                  color="#1D213B"
                  loading={loading}
                  size={screenView === 'mobile' ? 40 : 60}
                  aria-label="Loading Spinner"
                  data-testid="loader"
                  className="h-full w-full items-center justify-center"
                />
              </div>
            )}
          </div>
        );
    }
  };

  return (
    <>
      {userRecord &&
        userId &&
        userId?.length > 0 &&
        renderScreenView(screenView)}
      {newAddressPopupVisible && (
        <AddNewAddressModal
          onClose={() => setNewAddressPopupVisible(false)}
          addresses={sortedAddresses}
          isOpen={newAddressPopupVisible}
        />
      )}

      <Modal
        title="Confirm Account Deletion"
        open={isDeleteModalVisible}
        onOk={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        okText="Delete Account"
        okButtonProps={{
          danger: true,
          disabled: deleteConfirmText !== 'delete',
        }}
      >
        <p>Are you sure you want to submit a request to delete your account?</p>
        <Input
          value={deleteConfirmText}
          onChange={(e) => setDeleteConfirmText(e.target.value)}
          placeholder="Type 'delete' to confirm"
          className="mt-4"
        />
      </Modal>
    </>
  );
}

export default AccountSettings;
